import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { State } from "../../../store";
import EAButton from "./EAButton";

interface IProps {
  /**
   * Typ biletu/rezerwacji
   */
  ticketName?: string;
  /**
   * Funkcja wywoływana po kliknięciu w przycisk
   */
  onClick: () => void;
  /**
   * Czy przycisk ma być na całą szerokość kontenera
   */
  fullWidth?: boolean;
  /**
   * Wariant przycisku (filled, outlined, text)
   */
  variant?: "filled" | "outlined" | "text";
  /**
   * Czy sprawdzać warunek allowToChangeDate z konfiguracji
   */
  checkAllowToChangeDate?: boolean;
  /**
   * Czy sprawdzać czy nazwa biletu nie jest "REZERWACJA"
   */
  checkIfNotReservation?: boolean;
  /**
   * Ikona na końcu przycisku
   */
  endIcon?: ReactNode;
  /**
   * Czy przycisk jest wyłączony
   */
  disabled?: boolean;
  /**
   * Data rozpoczęcia ćwiczeń (potrzebna do sprawdzenia, czy możliwa jest zmiana terminu)
   */
  exerciseStartTime?: Date;
  /**
   * Czy sprawdzać czas przed rozpoczęciem ćwiczeń
   */
  checkTimeBeforeExercise?: boolean;
  /**
   * Obiekt ticket (potrzebny do sprawdzenia isExerciseSelfService)
   */
  ticket?: any;
  /**
   * Czy sprawdzać warunek isExerciseSelfService
   */
  checkIsExerciseSelfService?: boolean;
}

/**
 * Uniwersalny komponent przycisku "Zmień termin" używany w różnych miejscach aplikacji
 */
const ChangeTermButton: React.FC<IProps> = ({
  ticketName,
  onClick,
  fullWidth = false,
  variant = "filled",
  checkAllowToChangeDate = false,
  checkIfNotReservation = false,
  endIcon,
  disabled = false,
  exerciseStartTime,
  checkTimeBeforeExercise = false,
  ticket,
  checkIsExerciseSelfService = false,
}) => {
  const { t } = useTranslation();
  const allowToChangeDate = useSelector((state: State) => state.config.configuration.allowReservedExerciseChange);
  const blockTimeBeforeExerciseDateChange = useSelector(
    (state: State) => state.config.configuration.blockTimeBeforeExerciseDateChange
  );

  // Sprawdzamy czy przycisk powinien być renderowany
  if (checkIfNotReservation && ticketName === "REZERWACJA") {
    return null;
  }

  if (checkAllowToChangeDate && !allowToChangeDate && !(checkIsExerciseSelfService && ticket?.isExerciseSelfService)) {
    return null;
  }

  // Sprawdzamy czy jest wystarczająco dużo czasu przed ćwiczeniem do zmiany terminu
  if (checkTimeBeforeExercise && exerciseStartTime && blockTimeBeforeExerciseDateChange) {
    const now = new Date();
    const blockTime = new Date(exerciseStartTime);
    // Odejmujemy minuty z konfiguracji od czasu rozpoczęcia ćwiczenia
    blockTime.setMinutes(blockTime.getMinutes() - blockTimeBeforeExerciseDateChange);

    // Jeśli obecny czas jest większy niż czas blokady, nie pokazujemy przycisku
    if (now > blockTime) {
      return null;
    }
  }

  return (
    <EAButton
      title={t("zmien_termin")}
      onClick={onClick}
      fullWidth={fullWidth}
      variant={variant}
      endIcon={endIcon}
      disabled={disabled}
    />
  );
};

export default ChangeTermButton;
